import { useState } from "react";
import { Library, ScanSearch, AlertTriangle, CheckCircle2, Info } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { Field, NumField } from "@/components/Fields";
import { getLibrary, subtypes, subtypeOf, applySubtype, inferSubtype, designChecks, specOf } from "@/lib/iwin";
import { useCatalogues } from "@/context/CatalogueContext";
import { LibraryDialog } from "@/components/LibraryDialog";

const ICON = { error: AlertTriangle, warn: AlertTriangle, ok: CheckCircle2, info: Info };
const COLOR = { error: "text-rose-300", warn: "text-amber-300", ok: "text-emerald-300", info: "text-slate-400" };

export const IwinChecks = ({ item }) => (
  <ul className="space-y-1" data-testid="iwin-checks">
    {designChecks(item).map((c, i) => {
      const I = ICON[c.level];
      return <li key={i} data-testid={`iwin-check-${c.level}`} className={`flex items-start gap-1.5 text-[11px] ${COLOR[c.level]}`}><I size={12} className="mt-0.5 shrink-0" />{c.text}</li>;
    })}
  </ul>
);

const Chips = ({ opts, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-1">
    {opts.map(([v, l]) => (
      <button key={v} data-testid={`${testid}-${v}`} onClick={() => onChange(v)}
        className={`px-2 h-6 rounded-full border text-[10px] transition-colors ${String(value) === String(v) ? "border-cyan-400 bg-cyan-500/15 text-cyan-200" : "border-slate-700 text-slate-400 hover:border-slate-500"}`}>{l}</button>
    ))}
  </div>
);

const Options = ({ item, s, update }) => (
  <div className="space-y-2.5">
    {s.interlocks.length > 0 && <Field label="Interlock stile"><Chips testid="iwin-interlock" opts={s.interlocks.map((k) => [k, `${k}-type`])} value={item.interlock} onChange={(v) => update({ ...item, interlock: v })} /></Field>}
    <Field label="Design wind load"><Chips testid="iwin-wind" opts={Object.keys(s.limits).map((k) => [k, `${k} Pa`])} value={item.wind_load || Object.keys(s.limits)[0]} onChange={(v) => update({ ...item, wind_load: +v })} /></Field>
    {s.flyscreen_depth && <Field label="Flyscreen"><Chips testid="iwin-flyscreen" opts={[["no", `Without · ${s.depth} mm`], ["yes", `With · ${s.flyscreen_depth} mm`]]} value={item.flyscreen ? "yes" : "no"}
      onChange={(v) => update({ ...item, flyscreen: v === "yes", frame_depth: v === "yes" ? s.flyscreen_depth : s.depth })} /></Field>}
    {s.railing && <NumField label="Integrated railing height above sill (mm)" value={item.railing?.height ?? s.railing.height} testid="iwin-railing-height" min={600}
      onChange={(v) => update({ ...item, railing: { ...s.railing, ...(item.railing || {}), height: v } })} />}
  </div>
);

export const IwinBox = ({ item, update }) => {
  useCatalogues();
  const [open, setOpen] = useState(false);
  const lib = getLibrary();
  if (!lib) return null;
  const s = subtypeOf(item);
  const auto = () => { const k = inferSubtype(item); if (k) update(applySubtype(item, k, { layout: false })); };
  return (
    <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 space-y-3" data-testid="iwin-box">
      <div className="flex items-center gap-2">
        <p className="font-mono text-[10px] uppercase tracking-wider text-amber-200 flex-1">IWIN-S product · Bhoruka / YKK AP</p>
        <button data-testid="iwin-library-btn" onClick={() => setOpen(true)} className="h-6 px-2 rounded border border-slate-700 text-[10px] text-slate-200 flex items-center gap-1 hover:border-amber-400"><Library size={11} />Library</button>
      </div>
      <Select value={item.iwin || "none"} onValueChange={(k) => update(k === "none" ? { ...item, iwin: null } : applySubtype(item, k))}>
        <SelectTrigger data-testid="iwin-select" className="h-9 bg-[#0B0F19] border-slate-700 text-sm"><SelectValue placeholder="Select product" /></SelectTrigger>
        <SelectContent className="bg-[#111827] border-slate-700 text-slate-100 max-h-80">
          <SelectItem value="none" data-testid="iwin-option-none">Not matched</SelectItem>
          {lib.families.map(([f, label]) => (
            <SelectGroup key={f}><SelectLabel className="text-[10px] text-amber-300">{label}</SelectLabel>
              {subtypes().filter((x) => x.family === f).map((x) => <SelectItem key={x.key} value={x.key} data-testid={`iwin-option-${x.key}`}>{x.name}</SelectItem>)}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>
      <button data-testid="iwin-autodetect-btn" onClick={auto} className="w-full h-7 rounded-md border border-slate-700 text-[11px] text-slate-300 flex items-center justify-center gap-1.5 hover:border-amber-400 hover:text-amber-200">
        <ScanSearch size={12} />Auto-match from the drawn panels (keeps layout)
      </button>
      {s ? <>
        <p className="text-[11px] text-slate-400" data-testid="iwin-desc">{s.desc}</p>
        <p className="font-mono text-[10px] text-cyan-300" data-testid="iwin-spec">Depth {specOf(s, item).frame_depth} · frame {s.frame} · sash {s.sash} · {s.max_leaf_kg} kg/leaf · {s.joint}</p>
        <Options item={item} s={s} update={update} />
        <IwinChecks item={item} />
      </> : <p className="text-[11px] text-slate-500">Pick the exact IWIN-S product to apply its sightlines, depth, interlock, size limits and official section drawing.</p>}
      {open && <LibraryDialog current={item.iwin} onApply={(k) => { update(applySubtype(item, k)); setOpen(false); }} onClose={() => setOpen(false)} />}
    </div>
  );
};
